import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { ChevronLeft, ChevronRight, X, Bell, Calendar, TrendingUp, Sparkles, FileText, Users, Wallet, ArrowRight } from 'lucide-react';

const MASCOT = '/operio-mascot-cheer.png';
const STORAGE_KEY = (userId) => `operio-notifbar-dismissed-${userId}`;
const COLLAPSE_KEY = (userId) => `operio-notifbar-collapsed-${userId}`;

const DAILY_TIPS = [
  { id: 'tip-ai-buttons', tone: 'tip', icon: Sparkles, title: 'Visste du?', body: 'De 3 orange knapparna på dashboarden låter dig starta AI-beräkning med ett klick.', link: '/dashboard', cta: 'Visa' },
  { id: 'tip-invite-customer', tone: 'tip', icon: Users, title: 'Bjud in en kund', body: 'Dela projekt med din kund via mail — de ser progress och kostnader live i portalen.', link: '/dashboard/projects', cta: 'Projekt' },
  { id: 'tip-auto-book', tone: 'tip', icon: Wallet, title: 'AI sköter bokföringen', body: 'Ladda upp en faktura och låt AI:n bokföra automatiskt enligt BAS-kontoplanen.', link: '/dashboard/accounting', cta: 'Bokföring' },
  { id: 'tip-mascot', tone: 'tip', icon: Sparkles, title: 'Jag finns alltid här', body: 'Klicka på maskot-knappen nere till höger när du behöver hjälp — jag svarar direkt.', link: null, cta: null },
];

const NotificationBar = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState({});

  // Load dismissed/collapsed state per user
  useEffect(() => {
    if (!user) return;
    try {
      setDismissed(JSON.parse(localStorage.getItem(STORAGE_KEY(user.id)) || '{}'));
      const stored = localStorage.getItem(COLLAPSE_KEY(user.id));
      if (stored === null) {
        // Default: collapsed on mobile, expanded on desktop
        setCollapsed(true);
      } else {
        setCollapsed(stored === '1');
      }
    } catch (e) { /* ignore */ }
  }, [user]);

  const persistDismissed = useCallback((next) => {
    if (!user) return;
    localStorage.setItem(STORAGE_KEY(user.id), JSON.stringify(next));
  }, [user]);

  const buildItems = useCallback(async () => {
    if (!user || user.role !== 'admin') return [];
    const out = [];
    const now = new Date();
    const in7Days = new Date(now.getTime() + 7 * 86400000);

    // 1) Upcoming reminders / calendar
    try {
      const res = await api.getReminders({ status: 'pending' });
      const list = (res.data || [])
        .filter(r => r.due_date)
        .map(r => ({ ...r, _due: new Date(r.due_date) }))
        .filter(r => r._due >= new Date(now.toDateString()) && r._due <= in7Days)
        .sort((a, b) => a._due - b._due)
        .slice(0, 2);
      for (const r of list) {
        out.push({
          id: `reminder-${r.id}`,
          tone: 'calendar',
          icon: Calendar,
          title: 'Påminnelse',
          body: `${r.title}${r._due ? ` — ${r._due.toLocaleDateString('sv-SE')}` : ''}`,
          link: '/dashboard',
          cta: 'Visa',
        });
      }
    } catch (e) { /* ignore */ }

    // 2) Project milestones (progress crossed 25/50/75/100)
    try {
      const res = await api.getProjects();
      const projects = (res.data || []).slice(0, 10);
      for (const p of projects) {
        const pct = p.progress_percent || 0;
        if (pct >= 100) {
          out.push({
            id: `milestone-100-${p.id}`,
            tone: 'milestone',
            icon: TrendingUp,
            title: `${p.name} är klart!`,
            body: 'Projektet har nått 100 %. Dags att skicka slutfaktura?',
            link: `/dashboard/projects/${p.id}`,
            cta: 'Öppna',
          });
        } else if (pct >= 75) {
          out.push({
            id: `milestone-75-${p.id}`,
            tone: 'milestone',
            icon: TrendingUp,
            title: `${p.name} — 75 % klart`,
            body: 'Snart i mål. Glöm inte att fakturera ÄTA-arbeten.',
            link: `/dashboard/projects/${p.id}`,
            cta: 'Öppna',
          });
        }
      }
    } catch (e) { /* ignore */ }

    // 3) Daily tips
    out.push(...DAILY_TIPS);

    return out;
  }, [user]);

  useEffect(() => {
    let alive = true;
    buildItems().then(list => {
      if (!alive) return;
      const filtered = list.filter(i => !dismissed[i.id]);
      setItems(filtered);
      setIndex(0);
    });
    return () => { alive = false; };
  }, [buildItems, dismissed]);

  // Auto-rotate every 8s
  useEffect(() => {
    if (collapsed || items.length <= 1) return;
    const t = setInterval(() => setIndex(i => (i + 1) % items.length), 8000);
    return () => clearInterval(t);
  }, [collapsed, items.length]);

  const current = items[index];

  const dismissCurrent = () => {
    if (!current) return;
    const next = { ...dismissed, [current.id]: Date.now() };
    setDismissed(next);
    persistDismissed(next);
  };

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    if (user) localStorage.setItem(COLLAPSE_KEY(user.id), next ? '1' : '0');
  };

  // Hide on public/portal pages + customer/superadmin/login pages
  const hideOnRoute = useMemo(() => {
    const p = location.pathname;
    return p === '/' || p.startsWith('/login') || p.startsWith('/register') || p.startsWith('/customer') || p.startsWith('/superadmin') || p.startsWith('/about') || p.startsWith('/worker');
  }, [location.pathname]);

  if (!user || user.role !== 'admin' || hideOnRoute || items.length === 0) return null;

  const toneStyles = {
    calendar: { ring: 'ring-blue-500/30', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
    milestone: { ring: 'ring-[#E8613A]/40', badge: 'bg-[#E8613A]/10 text-[#E8613A]' },
    tip: { ring: 'ring-amber-500/30', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  };
  const tone = toneStyles[current?.tone] || toneStyles.tip;

  // Collapsed pill — just mascot + count
  if (collapsed) {
    return (
      <button
        onClick={toggleCollapse}
        className="hidden lg:flex fixed lg:bottom-4 lg:right-[96px] z-40 items-center gap-2 bg-card border border-border rounded-full pl-2 pr-3 sm:pr-4 py-1.5 shadow-xl hover:shadow-2xl transition-all"
        data-testid="notif-bar-expand-btn"
      >
        <img src={MASCOT} alt="" className="w-7 h-7 sm:w-8 sm:h-8 -my-2" />
        <span className="text-[11px] sm:text-xs font-semibold text-foreground">{items.length} {items.length === 1 ? 'notis' : 'notiser'}</span>
        <span className="w-1.5 h-1.5 bg-[#E8613A] rounded-full animate-pulse" />
      </button>
    );
  }

  if (!current) return null;
  const Icon = current.icon || Bell;

  return (
    <div
      className={`fixed bottom-[84px] lg:bottom-4 left-1/2 -translate-x-1/2 lg:left-auto lg:right-[96px] lg:translate-x-0 z-40 w-[min(560px,calc(100vw-2rem))] bg-card border border-border rounded-2xl shadow-2xl ring-2 ${tone.ring} flex items-stretch overflow-visible`}
      data-testid="notification-bar"
    >
      {/* Mascot peeking from the side */}
      <div className="relative w-20 sm:w-24 flex-shrink-0">
        <img
          src={MASCOT}
          alt="Operio"
          className="absolute -top-10 -left-1 sm:-left-2 w-24 sm:w-28 h-24 sm:h-28 drop-shadow-2xl pointer-events-none select-none"
          data-testid="notif-bar-mascot"
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 py-3 pr-3 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg ${tone.badge} flex items-center justify-center flex-shrink-0`}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-foreground truncate" data-testid="notif-title">{current.title}</p>
          <p className="text-xs text-muted-foreground line-clamp-2 leading-snug" data-testid="notif-body">{current.body}</p>
        </div>
        {current.link && current.cta && (
          <Link
            to={current.link}
            onClick={dismissCurrent}
            className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-[#E8613A] hover:text-[#D4532F] flex-shrink-0"
            data-testid="notif-cta"
          >
            {current.cta} <ArrowRight size={12} />
          </Link>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center border-l border-border">
        {items.length > 1 && (
          <>
            <button
              onClick={() => setIndex(i => (i - 1 + items.length) % items.length)}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Föregående"
              data-testid="notif-prev-btn"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-[10px] text-muted-foreground tabular-nums px-1">
              {index + 1}/{items.length}
            </span>
            <button
              onClick={() => setIndex(i => (i + 1) % items.length)}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Nästa"
              data-testid="notif-next-btn"
            >
              <ChevronRight size={14} />
            </button>
          </>
        )}
        <button
          onClick={dismissCurrent}
          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Avfärda"
          data-testid="notif-dismiss-btn"
          title="Avfärda detta meddelande"
        >
          <X size={14} />
        </button>
        <button
          onClick={toggleCollapse}
          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-tr-2xl rounded-br-2xl transition-colors"
          aria-label="Minimera"
          data-testid="notif-collapse-btn"
          title="Minimera notifikationsbaren"
        >
          <FileText size={14} />
        </button>
      </div>
    </div>
  );
};

export default NotificationBar;
